import { GlobalAlert, GlobalAlertsService, IGlobalAlert } from '@adaptiva/core/alerts';
import { INavSection, IProductSideNav, ProductNavigationService } from '@adaptiva/core/app-shell';
import { convertToDisplayDate, EnvService, filterNullish } from '@adaptiva/core/common';
import { Licensing } from '@adaptiva/core/licensing';
import { INotificationEvent, INotificationService, NotificationRegistration } from '@adaptiva/core/notifications';
import { DynamicOverlay, OverlayService } from '@adaptiva/core/overlays';
import { AuthenticationService, CloudUser, IPasswordChangeRequest, IUserProperties, LicenseManagerService, SystemConfigService } from '@adaptiva/data-service';
import { WhatsNewService } from '@adaptiva/server/common';
import { AppShellService, SideNavMenuController } from '@adaptiva/widgets/app-shell';
import { ChartService } from '@adaptiva/widgets/charts';
import { LicensedRouter } from '@adaptiva/widgets/licensed-router';
import { AfterViewInit, ChangeDetectionStrategy, Component, effect, EffectRef, inject, OnInit, Signal, viewChild, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, map, Observable, switchMap, take } from 'rxjs';

@Component({
	standalone: false,
	selector: 'ada-admin-portal-app-shell',
	templateUrl: './admin-portal-app-shell.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [SideNavMenuController]
})
export class AdminPortalAppShellComponent extends LicensedRouter implements OnInit, AfterViewInit {

	public currentProductName: string;

	public readonly sections: BehaviorSubject<INavSection[]> = new BehaviorSubject([]);

	public readonly menuController: SideNavMenuController = inject(SideNavMenuController);

	protected readonly autoActiviationNotification: NotificationRegistration = new NotificationRegistration('AutoActivationStatus');

	protected readonly alerts: GlobalAlertsService = inject(GlobalAlertsService);

	protected readonly auth: AuthenticationService = inject(AuthenticationService);

	protected readonly appShellService: AppShellService = inject(AppShellService);

	protected readonly overlayService: OverlayService = inject(OverlayService);

	protected readonly chartService: ChartService = inject(ChartService);

	protected readonly productNavigation: ProductNavigationService = inject(ProductNavigationService);

	protected readonly env: EnvService = inject(EnvService);

	protected readonly notifications: INotificationService = inject(INotificationService);

	protected readonly systemConfig: SystemConfigService = inject(SystemConfigService);

	protected readonly licenseManager: LicenseManagerService = inject(LicenseManagerService);

	protected readonly whatsNew: WhatsNewService = inject(WhatsNewService);

	protected readonly chartTooltipContainer: Signal<ViewContainerRef> = viewChild('ChartTooltipContainer', { read: ViewContainerRef });

	protected readonly chartTooltipContainerEffect: EffectRef = effect(() => {
		this.chartService.setGlobalTooltipContainer(this.chartTooltipContainer());
	});

	protected readonly chartTooltipEffect: EffectRef = effect(() => {
		const _containerElement: HTMLDivElement = this.chartTooltipContainer().element.nativeElement;

		_containerElement.togglePopover(this.chartService.showChartTooltip());
	});

	protected readonly copyrightMessage: Observable<string> = this.systemConfig.appDisplayName.pipe(
		map((_name: string) => {
			return `Copyright ${new Date().getFullYear()} ${_name}. All rights reserved.`;
		})
	);

	protected get globalSideNav(): IProductSideNav {
		return this.productNavigation.getProductSideNav('platform');
	}

	public override setNavItems(_productName: string): void {
		const _productNav: IProductSideNav = this.productNavigation.getProductSideNav(_productName);

		this.currentProductName = _productName;

		this.navItemsSubscription?.unsubscribe();
		this.navItemsSubscription = this.licenseManager.licenseStates.pipe(
			switchMap((_licenses: Map<string, Licensing.States>) => {
				return combineLatest({
					product: this.filterProduct(_productNav),
					global: this.filterProduct(this.globalSideNav)
				});
			}),
			this.takeUntilDestroyed()
		).subscribe({
			next: ({ product, global }) => {
				this.sections.next([
					...(product?.sections || []),
					...(global?.sections || [])
				]);
			},
			error: (e: Error) => {
				this.logger.error(
					'AdminPortalSideNavComponent._setCurrentProduct',
					'Failed to retrieve installed products to filter sidenav by licensing',
					this.logger.extractErrorMessage(e)
				)();
			}
		});
	}

	public override ngOnInit(): void {
		super.ngOnInit();
		this.alertBannerSetup();
		this.whatsNew.showBanner();
		this.licenseManager.checkExpiringProducts();
	}

	public ngAfterViewInit(): void {
		this.promptPasswordChange();
		this.licenseManager.checkEulas();
	}

	public override ngOnDestroy(): void {
		super.ngOnDestroy();
		this.notifications.unregisterNotification(this.autoActiviationNotification);
	}

	protected promptPasswordChange(): void {
		this.auth.user.pipe(
			filterNullish(),
			filter((_user: CloudUser) => this.auth.isPasswordExpiringSoon),
			this.takeOneUntilDestroyed(),
			switchMap((_user: CloudUser) => {
				return this.overlayService.confirm(
					'Password Expiring Soon',
					`Your password will expire on ${convertToDisplayDate(_user.passwordExpiryTime)}. ` +
					'Would you like to change your password now?'
				).pipe(
					switchMap((_confirmed: boolean) => {
						return this.overlayService.load<Partial<IUserProperties>, IPasswordChangeRequest>({
							overlayId: 'ChangePasswordOverlayComponent',
							context: { emailAddress: _user.loginId }
						});
					}),
					switchMap((_overlay: DynamicOverlay<IUserProperties, IPasswordChangeRequest>) => {
						return _overlay.submit;
					}),
					take(1)
				);
			})
		).subscribe();
	}

	protected alertBannerSetup(): void {
		this.notifications.listen(this.autoActiviationNotification).pipe(
			this.takeUntilDestroyed()
		).subscribe({
			next: (_notification: INotificationEvent) => {
				let _alert: IGlobalAlert;

				if (_notification.notificationQualifier === 'true') {
					_alert = new GlobalAlert({
						id: AdminPortalAppShellComponent.Alerts.AUTO_ACTIVATION_SUCCESS,
						type: GlobalAlertsService.Types.SUCCESS,
						title: 'Auto Activation Success',
						isDismissable: true,
						notify: true
					});
				} else {
					_alert = new GlobalAlert({
						id: AdminPortalAppShellComponent.Alerts.AUTO_ACTIVATION_FAILURE,
						type: GlobalAlertsService.Types.ERROR,
						title: 'Auto Activation Failure',
						details: _notification.value,
						isDismissable: true,
						notify: true
					});
				}

				this.alerts.set(_alert);
			}
		});
	}

}

export namespace AdminPortalAppShellComponent {
	export const enum Alerts {
		AUTO_ACTIVATION_SUCCESS = 'adaAlert-autoActivationSuccess',
		AUTO_ACTIVATION_FAILURE = 'adaAlert-autoActivationFailure',
	}
}
